import {Component} from 'react';
import PropTypes from 'prop-types';
import scheduleAnimationCallback from '../scheduleAnimationCallback';

export default class FocusManager extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    children: PropTypes.func.isRequired,
    onEscape: PropTypes.func.isRequired,
    assignContainerRef: PropTypes.func,
  };

  static defaultProps = {
    assignContainerRef() {},
  };

  componentDidMount() {
    // add a keydown listener
    document.addEventListener('keydown', this.handleKeyDown);
    if (this.props.isOpen && this.container) {
      // save reference of previous focused element, then focus the container
      this.previousActiveElement = this.getDocumentActiveElement();
      this.focusElement(this.container);
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isOpen && this.props.isOpen) {
      // save reference of previous focused element, then focus the container
      this.previousActiveElement = this.getDocumentActiveElement();
      this.focusElement(this.container);
      // if the focus manager was open and is no longer open
    } else if (prevProps.isOpen && !this.props.isOpen) {
      // then set focus to the element previously focused
      this.focusElement(this.previousActiveElement);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  container = null;
  lastTabbable = null;
  previousActiveElement = null;

  // wrapping `document.activeElement` getter in a method on the class so we
  // can easily mock this out during unit tests
  getDocumentActiveElement() {
    return document.activeElement;
  }

  handleKeyDown = (event) => {
    if (!this.props.isOpen) {
      // exit early if the focus manager isn't active
      return;
    }
    switch (event.key) {
      case 'Tab':
        this.handleTab(event);
        break;
      case 'Escape':
        this.props.onEscape();
        break;
    }
  };

  handleTab = (event) => {
    // The lastTabbable (aka close button) will always be last so we always redirect focus to the parent
    if (event.target === this.lastTabbable) {
      event.stopPropagation();
      event.preventDefault();
      this.focusElement(this.container);

      // If the user has tabbed to the container and they were holding shift (tabbing in reverse order)
      // then we want to focus the last tabbable element
      // (i.e. bring them to the "bottom" of the focus contained element)
    } else if (event.target === this.container && event.shiftKey) {
      event.stopPropagation();
      event.preventDefault();
      this.focusElement(this.lastTabbable);
    }
  };

  focusElement = (element) => {
    scheduleAnimationCallback(() => {
      if (element && typeof element.focus === 'function') {
        element.focus();
      }
    });
  };

  assignContainerRef = (element) => {
    this.props.assignContainerRef(element);
    this.container = element;
  };

  assignLastTabbableRef = (element) => {
    this.lastTabbable = element;
  };

  render() {
    return this.props.children({
      assignContainerRef: this.assignContainerRef,
      assignLastTabbableRef: this.assignLastTabbableRef,
    });
  }
}
